import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { filter, find, lowerFirst, transform } from 'lodash';
import { of } from 'rxjs';
import { catchError, exhaustMap, map, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';

import { selectUserGeneralRole } from '@app/modules/shared/selectors/user.selectors';
import { AdminPanelService } from '@app/services/admin-panel.service';
import { UserService } from '@app/services/user.service';
import { loadUserData } from '@shared/actions/user.actions';
import { AppRoutes } from '@shared/constants/app-routes.const';
import { GeneralRole } from '@shared/interfaces/general-role';
import { GeneralRoleToPermissionLevel } from '@shared/interfaces/general-role-to-permission-level';
import { PropertyBasicInfo } from '@shared/interfaces/propertyBasicInfo';
import { UserToGeneralRoleMapping } from '@shared/interfaces/user-to-general-role-mapping';
import { UserToGeneralRoleModel } from '@shared/interfaces/user-to-general-role-model';
import { UserToPropertyMapping } from '@shared/interfaces/user-to-property-mapping';
import { RestUserModel } from '@shared/interfaces/user.interface';
import { SnackbarErrorMessage } from '@ui-components/components/customized-snackbar/snackbar-message.enum';
import { SnackbarService } from '@ui-components/components/customized-snackbar/snackbar.service';
import { RestapiService } from 'src/app/services/restapi.service';

import * as AdministrationActions from '../actions/administration.actions';
import {
  addingNewUserFailed,
  addingNewUserSucceeded,
  assignUserToProperty,
  assignUserToPropertyError,
  assignUserToPropertySuccess,
  clearAllUsers,
  deleteGeneralRoleFailed,
  deleteGeneralRoleSucceeded,
  inviteNewUser,
  inviteNewUserError,
  inviteNewUserSuccess,
  loadAllGeneralRolesSucceeded,
  loadAllGeneralRolesToPermissionMappingSucceeded,
  loadAllPermissionLevelsSucceeded,
  loadAllPropertiesRolesSucceeded,
  loadAllUserToProperyMappingsSucceeded,
  loadAllUsers,
  loadAllUsersSucceeded,
  loadPropertiesStructure,
  loadPropertiesStructureSucceeded,
  loadPropertyUsersFailed,
  loadPropertyUsersSucceeded,
  loadUserToRoleMappingSucceeded,
  reactivateUser,
  reactivateUserSucceeded,
  removeUser,
  removeUserFailed,
  removeUserSucceeded,
  removingPropertyAssignedUser,
  removingPropertyAssignedUserError,
  removingPropertyAssignedUserSuccess,
  requestFailure,
  saveUserToGeneralRole,
  saveUserToGeneralRoleError,
  saveUserToGeneralRoleSuccess,
  savingGeneralRoleFailed,
  savingGeneralRoleSucceeded,
  setAllUsersCombinedWithGeneralRoles,
  startAddNewUser,
  startDeleteGeneralRole,
  startSavingGeneralRole,
  startUpdatingGeneralRole,
  updateUserData,
  updateUserDataSuccess,
  updatingGeneralRoleFailed,
  updatingGeneralRoleSucceeded,
} from '../actions/administration.actions';
import { DeleteRoleSummaryDialogComponent } from '../components/delete-role-summary-dialog/delete-role-summary-dialog.component';
import { combineUsersWithGeneralRoles } from '../functions/combineUsersWithRoles';
import {
  selectActiveUsers,
  selectAllGeneralRoles,
  selectAllUserToGeneralRoleMapping,
  selectAllUsers,
} from '../selectors/administration.selectors';

@Injectable()
export class AdministrationEffects {
  loadAllUsers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdministrationActions.loadAllUsers),
      exhaustMap(() => {
        return this.userService.getUserList().pipe(
          map(list =>
            loadAllUsersSucceeded({
              activeUsers: filter(list, { isActive: true }),
              inactiveUsers: filter(list, { isActive: false }),
            })
          ),
          catchError(error => {
            return of(requestFailure(error));
          })
        );
      })
    )
  );

  loadPropertyUsers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdministrationActions.loadPropertyUsers),
      exhaustMap(action => {
        return this.userService.getPropertyUserList(action.propertyId).pipe(
          map(list =>
            loadPropertyUsersSucceeded({
              activePropertyUsers: filter(list, { isActive: true }),
              inactivePropertyUsers: filter(list, { isActive: false }),
            })
          ),
          catchError(error => {
            return of(loadPropertyUsersFailed());
          })
        );
      })
    )
  );

  loadAllPermissionLevels$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdministrationActions.loadAllPermissionLevels),
      exhaustMap(() =>
        this.restapiService.getData<any>(`AdminPanel/PermissionLevels`).pipe(
          map(permissionLevels => loadAllPermissionLevelsSucceeded({ result: permissionLevels })),
          catchError(error => of(requestFailure(error)))
        )
      )
    )
  );

  loadAllGeneralRolesToPermissionMapping$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdministrationActions.loadAllGeneralRolesToPermissionMapping),
      exhaustMap(() =>
        this.restapiService.getData<GeneralRoleToPermissionLevel[]>(`AdminPanel/RolesToPermissions`).pipe(
          map(generalRoleToPermissionMapping =>
            loadAllGeneralRolesToPermissionMappingSucceeded({ result: generalRoleToPermissionMapping })
          ),
          catchError(error => of(requestFailure(error)))
        )
      )
    )
  );

  loadAllPropertyRoles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdministrationActions.loadAllPropertyRoles),
      exhaustMap(() =>
        this.restapiService.getData<GeneralRole[]>(`AdminPanel/PropertyRoles`).pipe(
          map(propertiesRoles => loadAllPropertiesRolesSucceeded({ result: propertiesRoles })),
          catchError(error => of(requestFailure(error)))
        )
      )
    )
  );

  loadUserToRoleMapping$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdministrationActions.loadUserToRoleMapping),
      exhaustMap(() =>
        this.restapiService.getData<UserToGeneralRoleMapping[]>(`AdminPanel/UserToGeneralRole/`).pipe(
          map(userToGeneralRole => loadUserToRoleMappingSucceeded({ userToGeneralRole })),
          catchError(error => of(requestFailure(error)))
        )
      )
    )
  );

  loadAllGeneralRoles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdministrationActions.loadAllGeneralRoles),
      exhaustMap(() =>
        this.restapiService.getData<GeneralRole[]>(`AdminPanel/GeneralRoles/`).pipe(
          map(generalRoles => loadAllGeneralRolesSucceeded({ generalRoles })),
          catchError(error => of(requestFailure(error)))
        )
      )
    )
  );

  loadAllUserToPropertyMappings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdministrationActions.loadAllUserToPropertyMappings),
      exhaustMap(() =>
        this.restapiService.getData<UserToPropertyMapping[]>(`AdminPanel/UserToPropertyRole/`).pipe(
          map(userToPropertyMapping => loadAllUserToProperyMappingsSucceeded({ userToPropertyMapping })),
          catchError(error => of(requestFailure(error)))
        )
      )
    )
  );

  combineUsersWithGeneralRoles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(clearAllUsers, loadAllUsersSucceeded, loadUserToRoleMappingSucceeded),
      withLatestFrom(this.store.select(selectAllUsers), this.store.select(selectAllUserToGeneralRoleMapping)),
      map(([, allUsers, allUserToGeneralRoleMapping]) => {
        if (!allUsers || !allUserToGeneralRoleMapping) {
          return setAllUsersCombinedWithGeneralRoles({ usersWithGeneralRoles: null });
        }

        const usersCombinedWithRoles = combineUsersWithGeneralRoles(allUsers, allUserToGeneralRoleMapping);

        return setAllUsersCombinedWithGeneralRoles({ usersWithGeneralRoles: usersCombinedWithRoles });
      }),
      catchError(error => of(requestFailure(error)))
    )
  );

  loadPropertiesStructure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadPropertiesStructure),
      exhaustMap(() => {
        return this.restapiService.getData<PropertyBasicInfo[]>(`AdminPanel/PropertiesStructure`).pipe(
          map(propertiesStructure => {
            return loadPropertiesStructureSucceeded({ propertiesStructure });
          }),
          catchError(error => of(requestFailure(error)))
        );
      })
    )
  );

  startAddingNewUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(startAddNewUser),
      mergeMap(action => {
        return this.restapiService.create<RestUserModel>(`Users`, action.user).pipe(
          map(user => addingNewUserSucceeded({ user })),
          catchError(error => {
            return of(requestFailure(error), addingNewUserFailed());
          })
        );
      })
    )
  );

  saveUserToGeneralRole$ = createEffect(() =>
    this.actions$.pipe(
      ofType(saveUserToGeneralRole),
      switchMap(action =>
        this.adminPanelService.setUserToGeneralRole(action.usersToGeneralRoleList).pipe(
          map(user => saveUserToGeneralRoleSuccess()),
          catchError(error => {
            this.snackbarService.error(SnackbarErrorMessage.ErrorAssigningUserToRole);
            return of(saveUserToGeneralRoleError());
          })
        )
      )
    )
  );

  saveUserToPropertyRole$ = createEffect(() =>
    this.actions$.pipe(
      ofType(assignUserToProperty),
      switchMap(action =>
        this.adminPanelService.setUserToPropertyRole(action.userToPropertyRoleList).pipe(
          map(user => assignUserToPropertySuccess()),
          catchError(error => {
            this.snackbarService.error(SnackbarErrorMessage.ErrorAssigningUserToProperty);
            return of(assignUserToPropertyError());
          })
        )
      )
    )
  );

  deleteUserToPropertyRole$ = createEffect(() =>
    this.actions$.pipe(
      ofType(removingPropertyAssignedUser),
      switchMap(action =>
        this.adminPanelService.removeUserFromPropertyRole(action.deleteUserToPropertyMappings).pipe(
          map(user => removingPropertyAssignedUserSuccess()),
          catchError(error => {
            this.snackbarService.error(SnackbarErrorMessage.ErrorRemovingPropertyAssignedUser);
            return of(removingPropertyAssignedUserError());
          })
        )
      )
    )
  );

  deleteGeneralRole$ = createEffect(() =>
    this.actions$.pipe(
      ofType(startDeleteGeneralRole),
      mergeMap(generalRole =>
        this.restapiService.create<any>(`AdminPanel/GeneralRoles/remove`, [generalRole.id]).pipe(
          withLatestFrom(this.store.select(selectAllGeneralRoles)),
          mergeMap(([, oldGeneralRoles]) => {
            const newGeneralRoles = oldGeneralRoles.filter(oldRole => oldRole.id !== generalRole.id);
            return [
              loadAllGeneralRolesSucceeded({ generalRoles: newGeneralRoles }),
              deleteGeneralRoleSucceeded(generalRole),
            ];
          }),
          catchError(error => of(deleteGeneralRoleFailed(error)))
        )
      )
    )
  );

  deleteGeneralRoleFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(deleteGeneralRoleFailed),
        withLatestFrom(this.store.select(selectActiveUsers), this.store.select(selectAllGeneralRoles)),
        tap(([result, allUsers, allGeneralRoles]) => {
          const feedback = result.error.message.split('Roles in use error: ')[1];
          const parsedFeedback = this.parseGeneralRoleFeedback(feedback);
          const usersWithRolesList = transform(
            parsedFeedback,
            (acc, entry) => {
              const user = find(allUsers, { id: entry.userId });
              const generalRole = find(allGeneralRoles, { id: entry.roleId });
              acc.push({ user, generalRole });
            },
            []
          );
          this.dialog.open(DeleteRoleSummaryDialogComponent, {
            autoFocus: false,
            data: usersWithRolesList,
            width: '620px',
          });
        })
      ),
    { dispatch: false }
  );

  startSavingGeneralRole$ = createEffect(() =>
    this.actions$.pipe(
      ofType(startSavingGeneralRole),
      mergeMap(({ generalRole }) =>
        this.restapiService.create<any>(`AdminPanel/GeneralRoles`, [generalRole]).pipe(
          tap(() => {
            this.router.navigate([AppRoutes.SETTINGS_ROLES]).then();
          }),
          map(() => savingGeneralRoleSucceeded()),
          catchError(error => of(savingGeneralRoleFailed(), requestFailure(error)))
        )
      )
    )
  );

  startUpdatingGeneralRole$ = createEffect(() =>
    this.actions$.pipe(
      ofType(startUpdatingGeneralRole),
      withLatestFrom(this.store.select(selectUserGeneralRole)),
      mergeMap(([{ generalRole }, currentUserGeneralRole]) =>
        this.restapiService.update<any>(`AdminPanel/GeneralRoles`, [generalRole]).pipe(
          tap(() => {
            this.router.navigate([AppRoutes.SETTINGS_ROLES]).then();
          }),
          mergeMap(() => {
            if (generalRole.roleName === currentUserGeneralRole) {
              this.userService.expireCashedUserData();
              return [updatingGeneralRoleSucceeded(), loadUserData()];
            }

            return [updatingGeneralRoleSucceeded()];
          }),
          catchError(error => of(updatingGeneralRoleFailed(), requestFailure(error)))
        )
      )
    )
  );

  requestFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(requestFailure),
        tap(error => {
          this.snackbarService.error(error.message);
        })
      ),
    { dispatch: false }
  );

  inviteNewUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(inviteNewUser),
      switchMap(action =>
        this.userService.inviteUser(action.user).pipe(
          map(user => {
            const userId = user.id;
            const roleId = action.roleId;
            const usersToGeneralRoleList: UserToGeneralRoleModel[] = [{ roleId, userId }];
            const userToPropertyRoleList: UserToPropertyMapping[] = action.selectedProperty.map<UserToPropertyMapping>(
              propertyId => {
                return {
                  propertyId,
                  userId,
                  roleId,
                  id: 0,
                };
              }
            );

            return { user, usersToGeneralRoleList, userToPropertyRoleList };
          }),
          mergeMap(param => [
            inviteNewUserSuccess({ user: param.user }),
            saveUserToGeneralRole({ usersToGeneralRoleList: param.usersToGeneralRoleList }),
            assignUserToProperty({ userToPropertyRoleList: param.userToPropertyRoleList }),
          ]),
          catchError(error => {
            const errorMessage = error?.error ?? '';
            const message = errorMessage.includes('User exists in Azure Active Directory')
              ? SnackbarErrorMessage.ErrorUserAlreadyExist.replace('{0}', action.user.email)
              : errorMessage.includes('One or more properties contains invalid values')
              ? SnackbarErrorMessage.ErrorWrongEmail
              : SnackbarErrorMessage.ErrorInvitingUser;
            this.snackbarService.error(message);
            return of(inviteNewUserError());
          })
        )
      )
    )
  );

  updateUserData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateUserData),
      switchMap(action =>
        this.userService.setUserData(action.user).pipe(
          map(user => {
            const userId = user.id;
            const roleId = action.roleId;
            const usersToGeneralRoleList: UserToGeneralRoleModel[] = [{ roleId, userId }];
            return usersToGeneralRoleList;
          }),
          mergeMap(usersToGeneralRoleList => [
            updateUserDataSuccess(),
            saveUserToGeneralRole({ usersToGeneralRoleList }),
            assignUserToProperty({ userToPropertyRoleList: action.userToPropertyRoleList }),
            removingPropertyAssignedUser({ deleteUserToPropertyMappings: action.deleteUserToPropertyMappings }),
          ]),
          catchError(error => {
            this.snackbarService.error(SnackbarErrorMessage.ErrorUpdatingUser);
            return of(savingGeneralRoleFailed());
          })
        )
      )
    )
  );

  removeUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(removeUser),
      mergeMap(action =>
        this.userService.removeUser(action.user).pipe(
          mergeMap(() => [removeUserSucceeded(), loadAllUsers()]),
          tap(() => {
            this.snackbarService.info(
              `Any of ${action.user.firstName}'s turns/tickets reassigned to their PM` as SnackbarErrorMessage
            );
          }),
          catchError(() => of(removeUserFailed()))
        )
      )
    )
  );

  reactivateUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(reactivateUser),
      mergeMap(action =>
        this.userService.reactivateUser(action.user).pipe(
          mergeMap(() => [reactivateUserSucceeded(), loadAllUsers()]),
          catchError(() => of(removeUserFailed()))
        )
      )
    )
  );

  constructor(
    private router: Router,
    private dialog: MatDialog,
    private actions$: Actions,
    private store: Store<{}>,
    private restapiService: RestapiService,
    private snackbarService: SnackbarService,
    private userService: UserService,
    private adminPanelService: AdminPanelService
  ) {}

  parseGeneralRoleFeedback(feedback: string): UserToGeneralRoleModel[] {
    const feedbackArray = feedback.split(';');
    return feedbackArray.map(entry => {
      return entry.split(',').reduce((acc, keyValuePair) => {
        const [key, value] = keyValuePair.split(':');
        acc[lowerFirst(key)] = parseInt(value, 10);
        return acc;
      }, {} as UserToGeneralRoleModel);
    });
  }
}
